import type { NamedOrg } from "@/lib/named-org";
import type { AxiosInstance } from "axios";
import type {
  AssistantSessionSummary,
  SessionsPage,
} from "@/components/codesmith/types";

export const ASSISTANT_SESSIONS_QUERY_KEY = "assistantSessions";
export function assistantSessionsQueryKey(org: NamedOrg | null) {
  return [
    ASSISTANT_SESSIONS_QUERY_KEY,
    org?.instanceSlug ?? null,
    org?.orgName ?? null,
  ] as const;
}

export const SESSIONS_PAGE_SIZE = 45;

/**
 * One pool of the sessions list response. Entries are lightweight
 * summaries; full session contents come from the per-session detail
 * endpoint. `non_migwiz_autofix_total` is `total` minus sessions that
 * exist only because the migration wizard enabled autofix on a
 * wizard-generated PR. The onboarding gate classifies fetched pool
 * sessions individually via their serialized `managed_prs` and uses this
 * aggregate to classify sessions beyond the fetched page (see
 * `onboardingRelevantSessionCount`).
 */
export type AssistantSessionsPoolResponse = SessionsPage & {
  non_migwiz_autofix_total?: number;
};

/**
 * Active sidebar pools ask the server to append any parent missing from the
 * page so a sub-session always nests under its parent row instead of
 * rendering as an orphan; see AssistantController::listSessions.
 */
export const WITH_ANCESTORS_PARAM = "&with_ancestors=1";

/**
 * Build the org-scoping query fragment for the sessions list endpoint.
 *
 * Prefer the numeric `installation_id` when known. When it isn't (a superuser
 * viewing a foreign org that isn't in their installations list), fall back to
 * the explicit `org` name so the backend scopes to that org directly instead
 * of the persisted `active_org_name`, which the URL-sync POST may not have
 * updated yet.
 */
export function buildSessionsScopeParam(
  installationId?: number | null,
  org?: NamedOrg | null,
): string {
  if (installationId) return `&installation_id=${installationId}`;
  if (org) {
    return `&org=${encodeURIComponent(
      org.orgName,
    )}&instance_slug=${encodeURIComponent(org.instanceSlug)}`;
  }
  return "";
}

export async function fetchAssistantSessions(
  api: AxiosInstance,
  installationId?: number | null,
  org?: NamedOrg | null,
  options?: {
    limit?: number;
    /** Mine/team pools drop automation runs; the sidebar groups them via useAutomationSessionGroups. */
    excludeAutomationSessions?: boolean;
  },
) {
  const limit = options?.limit ?? SESSIONS_PAGE_SIZE;
  const instParam = buildSessionsScopeParam(installationId, org);
  const excludeParam = options?.excludeAutomationSessions
    ? "&exclude_automation_sessions=1"
    : "";
  const [mineResponse, collaboratingResponse, teamResponse, archivedResponse] =
    await Promise.all([
      api.get(
        `/assistant/sessions?status=active&ownership=mine&limit=${limit}${instParam}${excludeParam}${WITH_ANCESTORS_PARAM}`,
      ),
      api.get(
        `/assistant/sessions?status=active&ownership=collaborating&limit=${limit}${instParam}${WITH_ANCESTORS_PARAM}`,
      ),
      api.get(
        `/assistant/sessions?status=active&ownership=team&limit=${limit}${instParam}${excludeParam}${WITH_ANCESTORS_PARAM}`,
      ),
      api.get(
        `/assistant/sessions?status=archived&ownership=mine&limit=${limit}${instParam}`,
      ),
    ]);
  return {
    mine: mineResponse.data as AssistantSessionsPoolResponse,
    collaborating: collaboratingResponse.data as AssistantSessionsPoolResponse,
    team: teamResponse.data as AssistantSessionsPoolResponse,
    archived: archivedResponse.data as AssistantSessionsPoolResponse,
  };
}
